import { useState, useEffect } from "react";

import { Shield, X, Check, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { ROUTES } from "@/lib/routes";

/**
 * Banner shown to users who have auto-generated communication codes
 * Prompts them to either accept the generated code or choose a custom one
 */
export const CommunicationCodeMigrationBanner = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showBanner, setShowBanner] = useState(false);
  const [currentCode, setCurrentCode] = useState<string>("");
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    const checkMigrationStatus = async () => {
      if (!user?.id) return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("communication_code, code_auto_generated")
        .eq("id", user.id)
        .single();

      // Show banner if code was auto-generated and user hasn't explicitly accepted it
      if (profile?.code_auto_generated && profile?.communication_code) {
        const dismissed = localStorage.getItem(`comm-code-migration-${user.id}`);
        if (!dismissed) {
          setShowBanner(true);
          setCurrentCode(profile.communication_code);
        }
      }
    };

    checkMigrationStatus();
  }, [user?.id]);

  const handleAcceptCode = async () => {
    if (!user?.id) return;
    
    setAccepting(true);
    try {
      // Mark code as no longer auto-generated (user has accepted it)
      const { error } = await supabase
        .from("profiles")
        .update({ code_auto_generated: false })
        .eq("id", user.id);

      if (error) throw error;

      // Mark as completed
      localStorage.setItem(`comm-code-migration-${user.id}`, 'accepted');
      setShowBanner(false);
    } catch (error) {
      console.error("Error accepting code:", error);
    } finally {
      setAccepting(false);
    }
  };

  const handleCustomize = () => {
    // Navigate to profile page where they can customize
    navigate(ROUTES.PROFILE);
    localStorage.setItem(`comm-code-migration-${user!.id}`, 'customizing');
    setShowBanner(false);
  };

  const handleDismiss = () => {
    // Temporarily dismiss (will show again next session unless accepted)
    localStorage.setItem(`comm-code-migration-${user!.id}`, 'dismissed_temp');
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="sticky top-0 z-50 border-b bg-linear-to-r from-blue-500/10 via-purple-500/10 to-blue-500/10 backdrop-blur-xs">
      <Alert className="rounded-none border-0 border-b bg-transparent">
        <div className="container max-w-7xl mx-auto">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-full bg-blue-500/20 mt-0.5">
              <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1 min-w-0">
              <AlertTitle className="mb-1 flex items-center gap-2">
                Your Security Code Has Been Set
                <Sparkles className="h-4 w-4 text-yellow-500" />
              </AlertTitle>
              <AlertDescription className="text-sm space-y-2">
                <p>
                  To protect you from phishing, we've assigned you a unique security code:{" "}
                  <span className="font-mono font-semibold text-blue-600 dark:text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded">
                    {currentCode}
                  </span>
                </p>
                <p className="text-xs text-muted-foreground">
                  This code appears in all our emails to you. If an email doesn't show your exact code, it's fake!
                </p>
              </AlertDescription>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                size="sm"
                variant="outline"
                onClick={handleCustomize}
                disabled={accepting}
              >
                Choose Custom Code
              </Button>
              <Button
                size="sm"
                onClick={handleAcceptCode}
                disabled={accepting}
              >
                <Check className="h-4 w-4 mr-1" />
                {accepting ? "Accepting..." : "Keep This Code"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleDismiss}
                disabled={accepting}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </Alert>
    </div>
  );
};
